/** @jsxImportSource @emotion/react */
import { ROUTES } from '@sportninja/common/constants/app'
import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { useHistory, useLocation } from 'react-router-dom'
import { useUsersMeUserRoles } from '@sportninja/common/hooks/useUsersMeUserRoles'
import {
  useUsersMeSummary,
  SummaryMembershipType,
} from '@sportninja/common/hooks/useUsersMeSummary'
import { Spin } from 'antd'
import Page from '../../components/Page'
import { connect } from 'react-redux'
import {
  getHockeySport,
  getSoccerSport,
} from '@sportninja/common/selectors/types'
import OrganizationModal from 'src/components/OrganizationModal'
import { NewModal } from 'src/components/NewModal'
import GeneratePluginModal from 'src/components/GeneralPluginModal'
import { t } from '@sportninja/common/i18n'
import OrgListTable from '../Orgs/OrgListTable'
import ScheduleListTable from '../Schedules/ScheduleListTable'
import TeamListTable from '../Teams/TeamListTable'

/**
 * MyMembership page component
 * Displays the user's memberships (organizations, schedules, teams)
 */
const MyMembership = ({ hockeySport, soccerSport }) => {
  const history = useHistory()
  const location = useLocation()
  const {
    data: userRoles,
    loading: rolesLoading,
    refetch,
  } = useUsersMeUserRoles()

  const [competitionsCount, setCompetitionsCount] = useState<number | string>(
    ''
  )
  const [organizationsCount, setOrganizationsCount] = useState<number | string>(
    ''
  )
  const [teamsCount, setTeamsCount] = useState<number | string>('')
  const [initialRouteResolved, setInitialRouteResolved] = useState(false)
  const [summaryLoaded, setSummaryLoaded] = useState(false)

  // Summary hook for fetching counts
  const { fetchSummary } = useUsersMeSummary()

  // Ref to track if initial summary has been fetched
  const hasFetchedInitialSummary = useRef(false)

  // Fetch initial counts on mount
  useEffect(() => {
    if (hasFetchedInitialSummary.current) {
      return
    }
    hasFetchedInitialSummary.current = true

    const loadInitialCounts = async () => {
      const result = await fetchSummary('permission')
      if (result) {
        setOrganizationsCount(result.organizations)
        setCompetitionsCount(result.schedules)
        setTeamsCount(result.teams)
      }
      setSummaryLoaded(true)
    }
    loadInitialCounts()
  }, [fetchSummary])

  // Handlers for membership type changes from each tab
  const handleOrgMembershipTypeChange = useCallback(
    async (type: SummaryMembershipType) => {
      const result = await fetchSummary(type)
      if (result) {
        setOrganizationsCount(result.organizations)
      }
    },
    [fetchSummary]
  )

  const handleScheduleMembershipTypeChange = useCallback(
    async (type: SummaryMembershipType) => {
      const result = await fetchSummary(type)
      if (result) {
        setCompetitionsCount(result.schedules)
      }
    },
    [fetchSummary]
  )

  const handleTeamMembershipTypeChange = useCallback(
    async (type: SummaryMembershipType) => {
      const result = await fetchSummary(type)
      if (result) {
        setTeamsCount(result.teams)
      }
    },
    [fetchSummary]
  )
  const hasHandledInitialRedirect = useRef(false)

  // Check if we're on the base route and need to determine initial tab
  const isOnBaseRoute = location.pathname === ROUTES.MY_MEMBERSHIP

  // Fetch user roles on mount
  useEffect(() => {
    refetch()
  }, [refetch])

  // Redirect to Teams tab if user has Player or Team Official roles (only on first load)
  useEffect(() => {
    if (
      isOnBaseRoute &&
      !rolesLoading &&
      userRoles &&
      !hasHandledInitialRedirect.current
    ) {
      hasHandledInitialRedirect.current = true
      const hasPlayerOrOfficialRole = userRoles.some((item) =>
        item.roles?.some((role) => role.role_id === 6 || role.role_id === 7)
      )
      const dontHasAnyOtherRoleThanPlayerOrOfficialRole = userRoles.every(
        (item) =>
          item.roles?.every((role) => role.role_id !== 6 && role.role_id !== 7)
      )
      if (
        hasPlayerOrOfficialRole &&
        dontHasAnyOtherRoleThanPlayerOrOfficialRole
      ) {
        history.replace(`${ROUTES.MY_MEMBERSHIP}/teams`)
      }
      setInitialRouteResolved(true)
    }
  }, [isOnBaseRoute, rolesLoading, userRoles, history])

  // If navigating directly to a sub-route or already handled redirect, resolve immediately
  useEffect(() => {
    if (!isOnBaseRoute || hasHandledInitialRedirect.current) {
      setInitialRouteResolved(true)
    }
  }, [isOnBaseRoute])

  const generatePluginTagModalRef = useRef<{
    openModal: () => void
    closeModal: () => void
  }>(null)
  const [selectedItem, setSelectedItem] = useState(null)
  const [organizationModalOpen, setOrganizationModalOpen] = useState(false)
  const [organization, setOrganization] = useState(null)

  const onClickWebPluginOnScheduleList = (schedule) => {
    setSelectedItem(schedule)
    generatePluginTagModalRef?.current?.openModal()
  }

  const onClickWebPluginOnTeamList = (team) => {
    setSelectedItem(team)
    generatePluginTagModalRef?.current?.openModal()
  }

  const pages = useMemo(
    () => [
      {
        to: `${ROUTES.MY_MEMBERSHIP}/organizations`,
        text: `${t('common:organization_plural', { count: 2 })} ${
          organizationsCount ? `(${organizationsCount})` : ''
        }`,
        component: (
          <OrgListTable
            refreshKey={'organizations-following'}
            key='organizations-following'
            slug='/users/me2/organizations'
            setInitialState={setOrganization}
            onEditOrg={() => {
              setOrganizationModalOpen(true)
            }}
            isMembership={true}
            onMembershipTypeChange={handleOrgMembershipTypeChange}
          />
        ),
      },
      {
        to: `${ROUTES.MY_MEMBERSHIP}/schedules`,
        text: `${t('common:competition_plural', { count: 2 })} ${
          competitionsCount ? `(${competitionsCount})` : ''
        }`,
        component: (
          // @ts-ignore
          <ScheduleListTable
            refreshKey={'competitions-following'}
            key='competitions-following'
            slug='/users/me2/schedules'
            alwaysListArchived={false}
            onClickWebPluginOnScheduleList={
              onClickWebPluginOnScheduleList as any
            }
            isMembership={true}
            onMembershipTypeChange={handleScheduleMembershipTypeChange}
            displayOrganizationColumn={true}
          />
        ),
      },
      {
        to: `${ROUTES.MY_MEMBERSHIP}/teams`,
        text: `${t('common:team_plural', { count: 2 })} ${
          teamsCount ? `(${teamsCount})` : ''
        }`,
        component: (
          <TeamListTable
            key='teams-following'
            slug='/users/me2/team-membership'
            // @ts-ignore
            soccerSport={soccerSport}
            hockeySport={hockeySport}
            onClickWebPluginOnTeamList={onClickWebPluginOnTeamList as any}
            isMembership={true}
            onMembershipTypeChange={handleTeamMembershipTypeChange}
            displayOrganizationColumn={true}
          />
        ),
      },
    ],
    [
      competitionsCount,
      handleOrgMembershipTypeChange,
      handleScheduleMembershipTypeChange,
      handleTeamMembershipTypeChange,
      hockeySport,
      organizationsCount,
      soccerSport,
      teamsCount,
    ]
  )

  const BASE_ROUTE = ROUTES.MY_MEMBERSHIP

  // Show loading while determining initial route or waiting for summary
  if ((isOnBaseRoute && !initialRouteResolved) || !summaryLoaded) {
    return (
      <div
        style={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          height: '100vh',
        }}
      >
        <Spin size='small' />
      </div>
    )
  }

  return (
    <>
      {/* @ts-ignore */}
      <Page
        baseRoute={BASE_ROUTE}
        // @ts-ignore
        pages={pages}
        baseTitle='Membership'
        title='My Membership'
        isOrgsPage={true}
        hideBreadcrumbs={true}
        displayMobileActions={false}
      />
      {/* @ts-ignore */}
      <OrganizationModal
        isFormOpen={organizationModalOpen}
        setIsFormOpen={setOrganizationModalOpen}
        organization={organization as any}
        setOrganization={setOrganization as any}
        refresh={() => {
          // Refresh logic if needed
        }}
      />
      <NewModal
        ref={generatePluginTagModalRef}
        shouldCloseOnOverlayClick={false}
      >
        <GeneratePluginModal
          onPressClose={() => {
            generatePluginTagModalRef?.current?.closeModal()
            setSelectedItem(null)
          }}
          // @ts-ignore
          scheduleId={selectedItem?.id}
          isOrganization={false}
          isTeam={false}
        />
      </NewModal>
    </>
  )
}

const mapStateToProps = (state) => {
  return {
    hockeySportId: getHockeySport(state)?.id,
    soccerSportId: getSoccerSport(state)?.id,
    hockeySport: getHockeySport(state),
    soccerSport: getSoccerSport(state),
  }
}

const mapDispatchToProps = (_) => {
  return {}
}

export default connect(mapStateToProps, mapDispatchToProps)(MyMembership)
